/**
 * Hook that persists comment draft text to localStorage.
 * Restores draft when the component mounts, clears on send.
 */

import { useState, useEffect, useCallback, useRef } from 'react'

const DRAFT_PREFIX = 'kobsign-comment-draft-'
const SAVE_DEBOUNCE_MS = 500

export function useCommentDraft(negotiationId: number, parentCommentId?: number, actorId?: string) {
  const actorSuffix = actorId ? `-${actorId}` : ''
  const key = parentCommentId
    ? `${DRAFT_PREFIX}${negotiationId}-reply-${parentCommentId}${actorSuffix}`
    : `${DRAFT_PREFIX}${negotiationId}${actorSuffix}`

  const [content, setContent] = useState(() => {
    try {
      return localStorage.getItem(key) || ''
    } catch {
      return ''
    }
  })

  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Debounced save to localStorage
  useEffect(() => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)

    saveTimeoutRef.current = setTimeout(() => {
      try {
        if (content.trim()) {
          localStorage.setItem(key, content)
        } else {
          localStorage.removeItem(key)
        }
      } catch {
        // localStorage may be full or blocked
      }
    }, SAVE_DEBOUNCE_MS)

    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
    }
  }, [content, key])

  const clearDraft = useCallback(() => {
    setContent('')
    try {
      localStorage.removeItem(key)
    } catch {
      // best-effort
    }
  }, [key])

  return { content, setContent, clearDraft }
}
